import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, TrendingUp, RefreshCw, KeyRound, Ban, Activity, Rocket, Webhook, RotateCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ApiUsageSection } from './ApiUsageSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_LABEL = {
  qr: 'QR share kit',
  local_seo: 'Local SEO',
  clinician_share: 'Clinician share',
  institutions_banner: 'Institutions banner',
  developers_page: 'Developer docs',
  dev_docs: 'Developer docs',
  direct: 'Direct',
};
const label = (c) => CHANNEL_LABEL[c] || c;

const Tile = ({ testid, label, value, tone }) => (
  <div data-testid={testid} className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800">
    <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    <p className={`text-2xl font-bold font-display mt-1 ${tone}`}>{value}</p>
  </div>
);

export const AttributionPanel = () => {
  const { token } = useAuth();
  const [attr, setAttr] = useState({ channels: [], totals: {} });
  const [keys, setKeys] = useState({ keys: [], count: 0, active: 0 });
  const [deliveries, setDeliveries] = useState({ deliveries: [], summary: {} });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [busyDelivery, setBusyDelivery] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, k, w, u] = await Promise.all([
        axios.get(`${API}/admin/attribution`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/api-keys`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/webhook-deliveries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/api-usage`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAttr(a.data);
      setKeys(k.data);
      setDeliveries(w.data);
      setUsage(u.data);
    } catch (e) {
      toast.error('Failed to load attribution');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const revoke = async (key) => {
    setBusyKey(key.id);
    try {
      await axios.post(`${API}/admin/api-keys/${key.id}/revoke`, {}, authHeader);
      toast.success(`Revoked ${key.org_name}'s key`);
      await load();
    } catch (e) {
      toast.error('Could not revoke key');
    } finally {
      setBusyKey(null);
    }
  };

  const promote = async (key) => {
    setBusyKey(key.id);
    try {
      await axios.post(`${API}/admin/api-keys/${key.id}/promote`, {}, authHeader);
      toast.success(`${key.org_name} promoted to production`);
      await load();
    } catch (e) {
      toast.error('Could not promote key');
    } finally {
      setBusyKey(null);
    }
  };

  const redeliver = async (d) => {
    setBusyDelivery(d.id);
    try {
      const { data } = await axios.post(`${API}/admin/webhook-deliveries/${d.id}/redeliver`, {}, authHeader);
      const st = data.delivery?.status;
      st === 'delivered' ? toast.success('Redelivered successfully') : toast.error(`Redelivery ${st || 'failed'}`);
      await load();
    } catch (e) {
      toast.error('Could not redeliver');
    } finally {
      setBusyDelivery(null);
    }
  };

  const t = attr.totals || {};
  const maxTotal = Math.max(1, ...(attr.channels || []).map((c) => c.total));

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>;
  }

  return (
    <div data-testid="attribution-panel">
      <div className="flex items-center justify-end mb-3">
        <button onClick={load} data-testid="attr-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 hover:text-cn-primary">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="attr-totals">
        <Tile testid="attr-total-leads" label="Family leads" value={t.leads ?? 0} tone="text-cn-primary" />
        <Tile testid="attr-total-inquiries" label="B2B inquiries" value={t.inquiries ?? 0} tone="text-violet-600" />
        <Tile testid="attr-total-converted" label="Converted" value={t.converted ?? 0} tone="text-emerald-600" />
        <Tile testid="attr-total-all" label="Total touches" value={t.total ?? 0} tone="text-slate-700 dark:text-slate-200" />
      </div>

      {/* Channels */}
      <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cn-primary" /> Conversions by channel</h3>
      {(attr.channels || []).length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="attr-empty">No tracked conversions yet. Tag funnels with <code>?ref=</code> to populate this.</p>
      ) : (
        <div className="space-y-2 mb-8">
          {attr.channels.map((c) => (
            <div key={c.channel} className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-3" data-testid={`attr-channel-${c.channel}`}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="font-semibold text-slate-900 dark:text-white text-sm">{label(c.channel)}</span>
                <span className="text-xs text-slate-500">
                  {c.leads > 0 && <span className="mr-2">{c.leads} leads</span>}
                  {c.inquiries > 0 && <span className="mr-2 text-violet-600">{c.inquiries} B2B</span>}
                  {c.converted > 0 && <span className="text-emerald-600 font-semibold">{c.converted} won</span>}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 dark:bg-cn-ink-700 overflow-hidden">
                <div className="h-full rounded-full bg-cn-primary" style={{ width: `${(c.total / maxTotal) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* API usage metrics (sales dashboard) */}
      <ApiUsageSection usage={usage} />

      {/* Developer keys */}
      <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-cn-primary" /> Discharge-API sandbox keys
        <span className="text-xs font-normal text-slate-500">({keys.active} active / {keys.count} total)</span>
      </h3>
      {keys.keys.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="attr-keys-empty">No sandbox keys issued yet. They're auto-minted when an institution requests API access.</p>
      ) : (
        <div className="space-y-2">
          {keys.keys.map((k) => {
            const prod = k.scope === 'production';
            return (
              <div key={k.id} className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`attr-key-${k.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-sm font-mono text-slate-700 dark:text-slate-200">{k.prefix}</code>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${prod ? 'bg-cn-primary text-white' : 'bg-slate-100 text-slate-600 dark:bg-cn-ink-700 dark:text-slate-300'}`} data-testid={`attr-key-tier-${k.id}`}>
                      {prod ? 'PRODUCTION' : 'sandbox'}
                    </span>
                    {k.revoked
                      ? <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-cn-ink-700">revoked</span>
                      : <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">active</span>}
                    {k.webhook_set && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 inline-flex items-center gap-1"><Webhook className="w-3 h-3" /> webhook</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{k.org_name} · {k.email}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Limits: {(k.rate_limit_per_min ?? 60)}/min · {(k.rate_limit_per_day ?? 5000).toLocaleString()}/day</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Activity className="w-3 h-3" /> {k.request_count} calls</span>
                  {!k.revoked && !prod && (
                    <button onClick={() => promote(k)} disabled={busyKey === k.id} data-testid={`attr-promote-${k.id}`}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-cn-primary/10 text-cn-primary hover:bg-cn-primary/20 inline-flex items-center gap-1 disabled:opacity-50" title="Promote to production tier">
                      {busyKey === k.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />} Promote
                    </button>
                  )}
                  {!k.revoked && (
                    <button onClick={() => revoke(k)} disabled={busyKey === k.id} data-testid={`attr-revoke-${k.id}`}
                      className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 disabled:opacity-50" title="Revoke key">
                      {busyKey === k.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Webhook delivery log */}
      <h3 className="text-sm font-bold text-slate-900 dark:text-white mt-8 mb-3 flex items-center gap-2">
        <Webhook className="w-4 h-4 text-cn-primary" /> Webhook deliveries
        <span className="text-xs font-normal text-slate-500">
          ({deliveries.summary?.delivered ?? 0} delivered · {deliveries.summary?.failed ?? 0} failed)
        </span>
      </h3>
      {(deliveries.deliveries || []).length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="attr-deliveries-empty">No webhook deliveries yet.</p>
      ) : (
        <div className="space-y-2" data-testid="attr-deliveries">
          {deliveries.deliveries.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`attr-delivery-${d.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs font-mono text-slate-700 dark:text-slate-200">{d.event}</code>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${d.status === 'delivered' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : d.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`} data-testid={`attr-delivery-status-${d.id}`}>
                    {d.status}{d.response_code ? ` · ${d.response_code}` : ''}
                  </span>
                  <span className="text-[10px] text-slate-400">{d.attempts} attempt{d.attempts === 1 ? '' : 's'}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{d.org_name} → <span className="font-mono">{d.url}</span></p>
              </div>
              {d.status !== 'delivered' && (
                <button onClick={() => redeliver(d)} disabled={busyDelivery === d.id} data-testid={`attr-redeliver-${d.id}`}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-cn-primary/10 text-cn-primary hover:bg-cn-primary/20 inline-flex items-center gap-1 disabled:opacity-50" title="Re-fire this delivery">
                  {busyDelivery === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />} Redeliver
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttributionPanel;
